/**
 * Generates random characters
 * @param prefix Prefix string
 * @param postfix Postfix string
 * @returns A random string with the prefix and postfix
 */

import { $ti } from '@/locize';
import type { ExitStatus, RaceContestant, TimeExitStatus, TimeType } from '@/objects/generated';
import { TimeSpan } from '@/objects/TimeSpan';
import type { ValueType } from '@/objects/ValueType';

export const randomstring = (prefix: string, postfix?: string) => {
    return prefix + Math.random().toString(36).substring(2) + (postfix || '');
};

/**
 * Gives a pseudorandom number with the specified number of digits
 *
 * This is not really random, for example, it will never start with a 0
 *
 * Can be used to generate simple verification codes etc.
 *
 * @param numberOfDigits Number of digits in the random number
 * @returns A pseudorandom number with the specified number of digits
 */
export const randomNumber = (numberOfDigits: number) => {
    const min = Math.pow(10, numberOfDigits - 1);
    const max = Math.pow(10, numberOfDigits) - 1;
    return Math.floor(Math.random() * (max - min + 1)) + min;
};

/**
 * Format the distance to a human readable string
 * @param distance
 */
export const formatDistance = (distance: number | null): string => {
    if (distance === null) {
        return '';
    }

    let translationkey = 'distance.kilometers';
    const _value: number = distance / 1000;
    let decimals = 2;

    if (distance < 1000) {
        translationkey = 'distance.meters';
        decimals = 0;
    }

    return $ti(translationkey, { value: _value.toFixed(decimals) });
};

/**
 * Creates a range of numbers represented as an array
 * @param start Start of range (inclusive)
 * @param end End of range (inclusive)
 * @returns A number array with all the numbers in the range
 */
export const range = (start: number, end: number) => {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
};

/**
 * Helper to make sure a string is a property of the object
 */
// export function propertyof<T>(key: keyof T, instance?: T): keyof T {
//     return key;
// }

// Utility type to get all nested property paths as string literals
type NestedKeyOf<T> = {
    [K in keyof T & (string | number)]: T[K] extends object ? K | `${K}.${NestedKeyOf<T[K]>}` : K;
}[keyof T & (string | number)];

/**
 * Helper function to have typesafe names of properties in an object.
 *
 * Supports nested keys
 *
 * @example
 * ```ts
 * interface Person {
 *   name: string;
 *   address: {
 *     street: string;
 *     city: string;
 *   }
 * }
 * const nameKey = propertyof<Person>('name'); // changing the name in the Person interface will cause a type error here
 * const streetKey = propertyof<Person>('address.street'); // changing the name of street in the Person interface will cause a type error here
 * ```
 */
export const propertyof = <T>(key: NestedKeyOf<T>): NestedKeyOf<T> => key;

/**
 * Helper to make sure a string is a toplevel property of T
 */
export const toplevelpropertyof = <T>(key: keyof T): keyof T => key;

/**

 * Helper to make sure a string is name of T
 */
export function nameofEnum<T>(name: T): string {
    return name as string;
}
/**
 * Helper method to get the actual type of a value
 * This need to be extended to support more types for now also nullable numbers are concidered text
 * @param value
 * @returns
 */
export function guessValueType(value: any): { guess: ValueType; actual: ValueType } {
    if (value instanceof TimeSpan) {
        return { guess: 'TimeSpan', actual: 'TimeSpan' };
    }

    if (value instanceof Date) {
        return { guess: 'Date', actual: 'Date' };
    }

    if (typeof value === 'boolean') {
        return { guess: 'Boolean', actual: 'Boolean' };
    }

    if (typeof value === 'number') {
        return { guess: 'Numeric', actual: 'Numeric' };
    }

    // We use a regexp to check
    if (!isNaN(value) && value != '') {
        // This is funky. We have already checked for NaN in the previous if, so what is left here is numbers in strings.
        // isNaN('') => false // This is actually a bug right? Empty strings are not numbers
        // isNaN('1') => false // This is the case we are actually testing for (?)
        // isNaN('a') => true // This is the case we are actually testing for (?)
        return { guess: 'Numeric', actual: 'Text' };
    }

    return { guess: 'Text', actual: 'Text' };
}

/**
 * swaps two properties in two objects
 * @param a
 * @param b
 * @param getter
 * @param setter
 */
export const swap = <TObj, TProp>(a: TObj, b: TObj, getter: (e: TObj) => TProp, setter: (e: TObj, v: TProp) => void) => {
    const temp = getter(a);
    setter(a, getter(b));
    setter(b, temp);
};

/**
 * finds two objects in a list and swaps one of their properties
 * @param list
 * @param from
 * @param filter
 * @param reduce
 * @param getter
 * @param setter
 * @returns
 */
export const swapInList = <TObj, TProp>(
    list: TObj[],
    from: TObj,
    filter: (e: TObj) => boolean,
    reduce: (selected: TObj, current: TObj) => TObj,
    getter: (e: TObj) => TProp,
    setter: (e: TObj, v: TProp) => void,
) => {
    const filtered = list.filter(filter);
    if (filtered.length === 0) {
        return null;
    }
    const to = filtered.reduce(reduce, filtered[0]);
    swap(from, to, getter, setter);
    return to;
};

/**
 * Helper method to get a SHA256 hash of a string
 * @param message
 * @returns
 */
export const sha256Hash = async (message: string) => {
    // encode as UTF-8
    const msgBuffer = new TextEncoder().encode(message);

    // hash the message
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);

    // convert ArrayBuffer to Array
    const hashArray = Array.from(new Uint8Array(hashBuffer));

    // convert bytes to hex string
    const hashHex = hashArray.map((b) => ('00' + b.toString(16)).slice(-2)).join('');
    return hashHex;
};

/**
 * Helper method to get a KnuthHash hash of a string
 * @see https://en.wikipedia.org/wiki/Hash_function#Knuth's_multiplicative_hash
 * @summary This is an implementation of the same we use in the backend Common
 * @param message
 * @returns
 */
export const KnuthHash = (str: string): bigint => {
    let hashedValue = 3074457345618258791n;
    for (let i = 0; i < str.length; i++) {
        hashedValue += BigInt(str.charCodeAt(i));
        hashedValue *= 3074457345618258799n;
    }
    return hashedValue;
};

/**
 * Capitalize the first letter of each word in a string
 * This is name "sensitive" so it will also try and capitalize words with - and ' i.e george-michael O'connor will be George-Michael O'Connor
 * @param str
 * @returns
 */
export const CapitalizeWords = (str: string | null): string => {
    if (str === null) {
        return '';
    }

    str = str.trim().toLowerCase();

    // Split words on spaces and capitalize each word
    let words = str
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');

    // We do the same with - and '
    words = words
        .split('-')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join('-');

    words = words
        .split("'")
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join("'");

    return words;
};

/**
 * ExitStatusSorter used by the ContestantListView.
 * This will sort in the order:
 *  DNC, DNQ, EXC, LAP, REL: -99999999999999
 *  DNS:                     -59999999999999
 *  DSQ:                     -49999999999999
 *  DNF:                     -39999999999999
 *  Finished:                -29999999999999
 *  ContestantTime:          +0
 *  RaceContestantTime:      +19999999999999
 * To these numbers the offset is added. The offset where this is used is ticks.
 * So we sort ExitStatuses first, then ContestantTimes (red), then RaceContestantTimes (green)
 * The time is added to the sortValue so ContestantTimes are internally sorted by their clocktime order.
 * So ExitStatus sort values are negative, while time values are positive
 */
export const ExitstatusSorter = (status: ExitStatus | TimeType, offset?: number): number => {
    return status == 'RaceContestantTime'
        ? 19999999999999 + (offset ?? 0)
        : status == 'ContestantTime'
          ? (offset ?? 0)
          : status == 'DNS'
            ? (59999999999999 + (offset ?? 0)) * -1
            : status == 'Finished'
              ? (29999999999999 + (offset ?? 0)) * -1
              : status == 'DNF'
                ? (39999999999999 + (offset ?? 0)) * -1
                : status == 'DSQ'
                  ? (49999999999999 + (offset ?? 0)) * -1
                  : (99999999999999 + (offset ?? 0)) * -1;
};

/**
 * Gives a numeric sortvalue for an ExitStatus or TimeType.
 *
 * ContestantTime, RaceContestantTime, Finished and None gives 0, since all these are for contestants that are "valid"
 * ExisStatuses that means the contestant has exited the race for some reason get a large positive number that is one order higher than ticks,
 * so that we can add the used time as an offset and get a correct sorting.
 *
 * This assumes that None (and similar) should be sorted infront of DNX, so they get a lower number
 *
 * @param status The status
 * @param offset Optional offset as numeric value that is added to the sort value so we can sort whatever we are sorting first by exitstatus
 * and then have a secondary sorting for those with the same exitstatus.
 * The offset can be ticks, the ExitValues are one order higher than ticks
 */
export const GetExitStatusSortValue = (status: ExitStatus | TimeType, offset?: number): number => {
    switch (status) {
        // TODO: Here ContestantTime and RaceContestantTime is considered equivalent, however a ContestantTime is "red", so maybe we should sort it differently
        case 'ContestantTime':
        case 'RaceContestantTime':
        case 'Finished':
        case 'None':
            return 0 + (offset ?? 0);
        case 'DNF':
            return 39_999_999_999_999 + (offset ?? 0);
        case 'DSQ':
            return 49_999_999_999_999 + (offset ?? 0);
        case 'DNS':
            return 59_999_999_999_999 + (offset ?? 0);
        // TODO: DNC, DNQ, EXC, LAP and REL should get values here
        default:
            return 99_999_999_999_999 + (offset ?? 0);
    }
};

/**
 * Helper method to get the z-index of an element
 * @param element
 * @returns
 */
export const GetElementZIndex = (element: HTMLElement): number => {
    if (element instanceof HTMLElement) {
        const style = window.getComputedStyle(element);
        return parseInt(style.zIndex || '0');
    }
    return 0;
};

/**
 * Find the highest z-index of all visible modals
 * @returns
 */
export const GetHighestModalZindex = (): number => {
    const modals = document.querySelectorAll('.modal-backdrop');
    let highest = 0;
    modals.forEach((modal) => {
        highest = Math.max(highest, GetElementZIndex(modal as HTMLElement));
    });
    return highest;
};

/**
 * Will sleep the thread for a given amount of time
 * @usage await ThreadSleep(1000); // Sleep for 1 second
 * @param ms
 * @returns
 */
export const ThreadSleep = (ms: number) => {
    return new Promise((resolve) => setTimeout(resolve, ms));
};

/**
 * Will format a number to a locale string
 * @param value
 * @param locale
 * @param options
 * @returns
 */
export const LocaleFormatNumeric = (value: number, locale?: string, options?: Intl.NumberFormatOptions): string => {
    // if locale is not provided we use the default browser locale
    if (!locale) {
        locale = navigator.language;
    }

    return new Intl.NumberFormat(locale, options).format(value);
};

/**
 * Creates a shots array from a number of misses
 *
 * Example: `shootingMissesToShotsArray(3) => [1, 1, 1, 0, 0]`
 * @param misses the number of misses
 * @returns an array of 1s and 0s where 1 is a miss and 0 is a hit, with the misses first
 */
export const shootingMissesToShotsArray = (misses: number): number[] => Array.from({ length: 5 }, (_, i) => (i < misses ? 1 : 0));

/**
 *
 * @param stream Shows a stream in a new window or downloads it depending on the content type
 * @param contentType
 * @param filename
 */
export const showStreamInNewWindow = async (stream: ReadableStream<Uint8Array>, contentType: string | null, filename?: string) => {
    if (contentType === 'application/pdf') {
        await showPdfFromStreamInNewWindow(stream);
    } else if (contentType === 'text/csv') {
        await downloadCsvFromStream(stream, filename ?? 'report.csv');
    } else if (contentType === 'application/octet-stream') {
        await downloadFileFromStream(stream, filename ?? 'file.bin');
    } else {
        throw new Error('filetype.unsupported', { cause: '' });
    }
};

/**
 * Will try and read a stream as text
 * @param stream
 * @returns
 */
export const readStreamAsText = async (stream: ReadableStream<Uint8Array>): Promise<string> => {
    // We need to convert the stream into text
    const reader = stream.getReader();
    let result = '';
    let done = false;
    while (!done) {
        const { value, done: doneReading } = await reader.read();
        if (value) {
            result += new TextDecoder('utf-8').decode(value);
        }
        done = doneReading;
    }
    return result;
};

/**
 * Read a stream and open it in a new window as a pdf
 * @param stream
 */
export const showPdfFromStreamInNewWindow = async (stream: ReadableStream<Uint8Array>) => {
    // Convert the ReadableStream to a Blob
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let result = await reader.read();
    while (!result.done) {
        chunks.push(result.value);
        result = await reader.read();
    }
    const blob = new Blob(chunks as BlobPart[], { type: 'application/pdf' });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Open a new window and display the PDF
    const newWindow = window.open(url);

    if (!newWindow || newWindow.closed || typeof newWindow.closed == 'undefined') {
        // useToastStore().addWarning('popups.blocked');
        throw new Error('popups.blocked', { cause: '' });
    } else {
        // Revoke the object URL when the window is closed, since we don't need it anymore then
        // We have to keep it around until the window is closed, otherwise its not possible to download the file
        newWindow.onclose = () => {
            URL.revokeObjectURL(url);
        };
    }
};

/**
 * Read a stream and open it in a new window as a csv
 * @param stream
 */
export const downloadCsvFromStream = async (stream: ReadableStream<Uint8Array>, filename: string) => {
    // Convert the ReadableStream to a Blob
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let result = await reader.read();
    while (!result.done) {
        chunks.push(result.value);
        result = await reader.read();
    }
    const blob = new Blob(chunks as BlobPart[], { type: 'text/csv' });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // TRIGGER IT AS A DOWNLOAD
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
};

export const downloadPdfFromStream = async (stream: ReadableStream<Uint8Array>, filename: string) => {
    // Convert the ReadableStream to a Blob
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let result = await reader.read();
    while (!result.done) {
        chunks.push(result.value);
        result = await reader.read();
    }
    const blob = new Blob(chunks as BlobPart[], { type: 'application/pdf' });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // TRIGGER IT AS A DOWNLOAD
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    URL.revokeObjectURL(url);
};

/**
 * No operation function
 * Can be used where a function is required but the source is optional so it can be undefined
 */
export const noop = () => {};

/**
 * This will force the browser to download a file from a stream instead of showing it.
 * @param stream
 * @param filename
 */
export const downloadFileFromStream = async (stream: ReadableStream<Uint8Array>, filename: string) => {
    // Convert the ReadableStream to a Blob
    const reader = stream.getReader();
    const chunks: Uint8Array[] = [];
    let result = await reader.read();
    while (!result.done) {
        chunks.push(result.value);
        result = await reader.read();
    }
    const blob = new Blob(chunks as BlobPart[], { type: 'application/octet-stream' });

    // Create a URL for the Blob
    const url = URL.createObjectURL(blob);

    // Create a link element and trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();

    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

/**
 * Formats a byte length to a human readable string
 * @example formatByteSize(52000) => "50.78 kB", or formatByteSize(5000000) => "4.77 MB"
 * @param bytes
 * @returns
 */
export const formatByteSize = (bytes: number): string => {
    let unit: string = 'B';

    // We dont need decimals when the value is less than 1kB
    const fixedNumber: number = bytes >= 1024 ? 2 : 0;
    if (bytes >= 1024) {
        bytes /= 1024;
        unit = 'kB';
    }
    if (bytes >= 1024) {
        bytes /= 1024;
        unit = 'MB';
    }
    if (bytes >= 1024) {
        bytes /= 1024;
        unit = 'GB';
    }
    if (bytes >= 1024) {
        bytes /= 1024;
        unit = 'TB';
    }
    return `${bytes.toFixed(fixedNumber)} ${unit}`;
};

/**
 * Converts a TimeExitStatus to a TimeType or throws an error if the status is unknown
 * @param status
 * @returns
 * @throws Error if the status is unknown
 */
export const timeExitStatusToTimeType = (status: TimeExitStatus): TimeType => {
    switch (status) {
        case 'None':
            return 'None';
        case 'LAP':
            return 'LAP';
        case 'DNS':
            return 'DNS';
        case 'Finished':
            return 'Finished';
        case 'DNF':
            return 'DNF';
        case 'DSQ':
            return 'DSQ';
        case 'DNQ':
            return 'DNQ';
        case 'DNC':
            return 'DNC';
        case 'REL':
            return 'REL';
        case 'Deleted':
            return 'None';
        default:
            throw new Error(`Unknown TimeExitStatus: ${status}`);
    }
};

/**
 * Get the order value for a time exit status. This is used to sort the rows when we have an exit status. We want to sort DNF first, then DNS, etc.
 * @param status
 */
export const getTimeExitStatusOrderValue = (status?: TimeExitStatus | null): number => {
    if (!status) {
        return 0;
    }

    if (status === 'DNF') {
        return Number.MAX_SAFE_INTEGER - 100;
    } else if (status === 'DSQ') {
        return Number.MAX_SAFE_INTEGER - 80;
    } else if (status === 'DNQ') {
        return Number.MAX_SAFE_INTEGER - 60;
    } else if (status === 'REL') {
        return Number.MAX_SAFE_INTEGER - 40;
    } else if (status === 'DNS') {
        return Number.MAX_SAFE_INTEGER - 20;
    }

    return 0;
};

/**
 * Converts a RaceContestant's exit status to a TimeType
 * @param rc
 * @returns
 */
export const raceContestantExitStatusToTimeExitStatus = (rc: RaceContestant): TimeExitStatus => {
    if (rc.DNC) {
        return 'DNC';
    } else if (rc.DNS) {
        return 'DNS';
    } else if (rc.DNF) {
        return 'DNF';
    } else if (rc.DSQ) {
        return 'DSQ';
    } else if (rc.DNQ) {
        return 'DNQ';
    } else if (rc.LAP) {
        return 'LAP';
    } else if (rc.COMPLETED) {
        return 'Finished';
    } else if (rc.REL) {
        return 'REL';
    } else {
        return 'Time';
    }
};

/**
 * Converts a RaceContestant's exit status to a TimeType
 * @param rc
 * @returns
 */
export const raceContestantExitStatusToExitStatus = (rc: RaceContestant): ExitStatus => {
    if (rc.DNC) {
        return 'DNC';
    } else if (rc.DNS) {
        return 'DNS';
    } else if (rc.DNF) {
        return 'DNF';
    } else if (rc.DSQ) {
        return 'DSQ';
    } else if (rc.DNQ) {
        return 'DNQ';
    } else if (rc.LAP) {
        return 'LAP';
    } else if (rc.COMPLETED) {
        return 'Finished';
    } else if (rc.REL) {
        return 'REL';
    } else {
        return 'None';
    }
};

export const isLocalHost = (): boolean => import.meta && import.meta.env && import.meta.env.MODE === 'development';

export const isNextDev = (): boolean => {
    return document.location.hostname === 'nextdev.eqtiming.com';
};
